import { getRouteApi } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { useState } from "react";
import type { PublicAd } from "@/lib/agentmrr-ads";
import { buildOutboundUrl } from "@/lib/outbound-url";

const rootRoute = getRouteApi("__root__");

/** The rotating sponsor pool (active paid placements) from the root loader. */
export function useSponsors(): PublicAd[] {
	return rootRoute.useLoaderData();
}

/** Derive the favicon host from an ad's destination URL. */
function adHost(url: string): string | null {
	try {
		const withProtocol = /^https?:\/\//i.test(url) ? url : `https://${url}`;
		const { hostname } = new URL(withProtocol);
		return hostname || null;
	} catch {
		return null;
	}
}

/**
 * Sponsor logo resolved from the destination favicon via `/api/logo`, with a
 * mono letter fallback on error. PublicAd carries no logo of its own.
 */
function SponsorLogo({ ad, className }: { ad: PublicAd; className: string }) {
	const [errored, setErrored] = useState(false);
	const host = adHost(ad.url);
	const fallback = (
		<div
			className={`${className} flex items-center justify-center border border-border bg-muted font-mono text-foreground`}
		>
			{ad.name.charAt(0).toUpperCase()}
		</div>
	);
	if (!host || errored) return fallback;
	return (
		<img
			src={`/api/logo?host=${encodeURIComponent(host)}`}
			alt=""
			className={`${className} object-contain`}
			loading="lazy"
			decoding="async"
			onError={() => setErrored(true)}
		/>
	);
}

/**
 * A sponsored leaderboard row spanning the full table width. Visually distinct
 * from ranked rows while keeping the same horizontal rhythm.
 */
export function SponsoredLeaderboardRow({
	ad,
	colSpan,
}: {
	ad: PublicAd;
	colSpan: number;
}) {
	return (
		<tr className="bg-brand-soft">
			<td colSpan={colSpan} className="border-t border-border/60 px-2 py-1.5">
				<a
					href={buildOutboundUrl(ad.url, "sponsor_row")}
					target="_blank"
					rel="noopener noreferrer sponsored"
					className="group flex items-center gap-3"
				>
					<SponsorLogo ad={ad} className="size-8 shrink-0" />
					<div className="min-w-0 flex-1">
						<div className="flex min-w-0 items-center gap-2">
							<p
								className="truncate text-base text-foreground transition-colors group-hover:text-brand"
								style={{ fontWeight: 400 }}
							>
								{ad.name}
							</p>
							<span className="shrink-0 border border-border px-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
								Sponsored
							</span>
						</div>
						{ad.description && (
							<p className="line-clamp-1 text-sm text-muted-foreground">
								{ad.description}
							</p>
						)}
					</div>
					<ArrowUpRight className="size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-brand" />
				</a>
			</td>
		</tr>
	);
}

/** "Want more reach?" sponsored recommendation for agent profiles. */
export function SponsoredRecommendation({ ad }: { ad: PublicAd }) {
	return (
		<section className="mt-12">
			<div className="flex items-baseline justify-between pb-3">
				<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; Want more reach?
				</span>
				<span className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
					Sponsored
				</span>
			</div>
			<a
				href={buildOutboundUrl(ad.url, "sponsor_profile")}
				target="_blank"
				rel="noopener noreferrer sponsored"
				className="group flex items-center gap-3 border border-border bg-background p-4 transition-colors hover:border-brand/40 hover:bg-brand-soft"
			>
				<SponsorLogo ad={ad} className="size-10 shrink-0" />
				<div className="min-w-0 flex-1">
					<p
						className="truncate text-base text-foreground transition-colors group-hover:text-brand"
						style={{ fontWeight: 400 }}
					>
						{ad.name}
					</p>
					{ad.description && (
						<p className="line-clamp-1 text-sm text-muted-foreground">
							{ad.description}
						</p>
					)}
				</div>
				<ArrowUpRight className="size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-brand" />
			</a>
		</section>
	);
}

/** Stable index from a string key so a given profile always shows one sponsor. */
export function sponsorIndexForKey(key: string): number {
	let hash = 0;
	for (let i = 0; i < key.length; i++) {
		hash = (hash * 31 + key.charCodeAt(i)) | 0;
	}
	return Math.abs(hash);
}
